import {
  areFieldsDirty,
  buildAllFieldsFromSections,
  buildFormValuesFromFields,
  buildPayloadFromFields,
  markSeededDefaultsDirty,
  Card,
  CardContent,
  Form,
  FormBuilderSections,
  getResolver,
  LoadingCard,
  QuoteDerogationLockNotice,
  resolveSectionsFieldShortcuts,
  toast,
  useStepperContext,
} from '@folio/components';
import { FormBuilderFooter } from '@folio/components/forms/form-builder/FormBuilderFooter';
import { skipsStartedAtRelativeMinimum } from '@folio/utils/skipStartedAtRelativeMinimum';
import { isQuoteDerogationApproved } from '@folio/utils/quoteDerogationLock';
import { useQuery } from '@tanstack/react-query';
import {
  Asset,
  BranchDto,
  BrokerageFeeOnPolicy,
  clientWithFork,
  configControllerGetPublicProductConfigQueryOptions,
  Customer,
  customerControllerGetCustomerQueryOptions,
  CustomFieldDto,
  derogationControllerCheckDerogationRequirementQueryOptions,
  Duration,
  EditPolicyInputBody,
  GetBrokerageFirmOutputFirmTypeEnumKey,
  getNewBranchIdAfterFork,
  keyValueFieldsToObject,
  Policy,
  QuoteConfig,
  useAssetControllerEditAsset,
  usePolicyControllerEditPolicy,
  usePolicyControllerManualQuotePolicy,
  usePolicyControllerSetPolicyFees,
  usePolicyControllerUpdatePolicyQuote,
} from 'korint-sdk';
import { Fields, FormSections, FormSectionsWithShortcuts } from 'korint-sdk/react';
import { useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { isEmpty } from 'lodash';
import { QuoteRecapModule } from '../QuoteRecapModule/QuoteRecapModule';
import { useCalculateQuotes } from '../shared-quote/use-calculate-quotes';
import { QuoteContext } from '../shared-quote/QuoteContext';
import {
  getAdditionalQuoteComponentsFromFormPayload,
  getAdditionalQuotePercentagesFromFormPayload,
  earlierBound,
  laterBound,
  resolveAssetStartedAtBounds,
} from '../shared-quote/utils';
import { DocumentsUploadPolicyModule } from '../DocumentsUploadPolicyModule/DocumentsUploadPolicyModule';
import { WaitingForQuoteCard } from './WaitingForQuoteCard';
import { Stack } from '@folio/components/ui/flex';

const EMPTY_FIELDS: Fields = [];
const EMPTY_SECTIONS: FormSections = [];
const EMPTY_ADDITIONAL_SECTIONS: Array<FormSections[number] & { forTypes?: string[] }> = [];
const EMPTY_DOCUMENTS: string[] = [];
const EMPTY_ASSET_IDS: string[] = [];

type NonDelegatedModuleProps = {
  mode?: 'stepper' | 'standalone';
  showPreviousButton?: boolean;
  showQuoteRecap?: 'aside' | 'inline' | false;
  firmType: GetBrokerageFirmOutputFirmTypeEnumKey | undefined;
  policy: Policy;
  branch: BranchDto;
  asset: Asset;
  assets: Asset[];
  quoteConfig: QuoteConfig;
  policySections?: FormSectionsWithShortcuts;
  assetSections?: FormSectionsWithShortcuts;
  additionalQuoteComponentsSections?: Array<FormSections[number] & { forTypes?: string[] }>;
  manualQuoteSections?: FormSections;
  additionalComponent?: React.ReactNode;
  nonDelegatedDocuments?: string[];
  helper?: React.ReactNode;
  disabled?: boolean;
};

export function NonDelegatedQuoteSections({
  mode = 'stepper',
  showPreviousButton = true,
  showQuoteRecap = 'aside',
  firmType,
  policy,
  branch,
  asset,
  assets,
  quoteConfig,
  policySections: rawPolicySections = EMPTY_SECTIONS,
  assetSections: rawAssetSections = EMPTY_SECTIONS,
  manualQuoteSections: rawManualQuoteSections = EMPTY_SECTIONS,
  additionalQuoteComponentsSections:
    rawAdditionalQuoteComponentsSections = EMPTY_ADDITIONAL_SECTIONS,
  nonDelegatedDocuments = EMPTY_DOCUMENTS,
  additionalComponent,
  helper,
  disabled,
}: NonDelegatedModuleProps) {
  const stepper = useStepperContext();

  const { data: customer } = useQuery({
    ...customerControllerGetCustomerQueryOptions(policy.customers?.[0] ?? '', {
      branchId: branch.id,
    }),
    select: data =>
      ({ ...data, customFields: keyValueFieldsToObject(data.customFields ?? []) }) as Customer,
    enabled: Boolean(policy.customers?.[0]),
  });

  const assetIds = useMemo(() => assets?.map(asset => asset.id) ?? EMPTY_ASSET_IDS, [assets]);

  const { data: needsDerogation, isLoading: isCheckingDerogationLoading } = useQuery({
    ...derogationControllerCheckDerogationRequirementQueryOptions(
      branch?.id!,
      {},
      {
        action: 'QUOTE',
      },
    ),
    select: data => data.needsDerogation,
    enabled: Boolean(branch?.id),
  });

  // needsDerogation checks if user is wholesale in case of non delegated quote.
  // Also show when branch type is RENEWAL.
  const showWaitingForQuote =
    (policy.status !== 'POLICY_QUOTED' || branch?.branchType === 'RENEWAL') &&
    needsDerogation &&
    !isQuoteDerogationApproved(branch);

  const {
    data: {
      policyFields: policyFieldsConfig,
      assetFields: assetFieldsConfig,
      assetStartedAtLimit,
    } = {},
  } = useQuery({
    ...configControllerGetPublicProductConfigQueryOptions(policy.productId),
  });

  const policySections: FormSections = useMemo(
    () =>
      resolveSectionsFieldShortcuts(rawPolicySections, policyFieldsConfig ?? [])
        .filter(section => !(section.forTypes && !section.forTypes.includes(firmType ?? '')))
        .map(section => {
          const fields = Array.from(section.fields);

          const hasBrokerageFirmField = section.fields.some(
            field => field.name === 'brokerageFirm',
          );

          if (!hasBrokerageFirmField) {
            fields.push({
              name: 'brokerageFirm',
              type: 'text',
              hidden: true,
              ignoreInPayload: true,
              required: false,
            } as const);
          }

          const hasInvoicingFrequencyField = section.fields.some(
            field => field.name === 'invoicingConfig.frequency',
          );

          if (!hasInvoicingFrequencyField) {
            fields.push({
              name: 'invoicingConfig.frequency',
              type: 'text',
              hidden: true,
              ignoreInPayload: true,
              required: false,
              apiDataAccessor: 'invoicingConfig.frequency',
            } as const);
          }

          return {
            ...section,
            fields,
          };
        })
        .filter(section => section.fields.some(f => !f.hidden)),
    [rawPolicySections, policyFieldsConfig, firmType],
  );

  const skipRelativeMinimum = skipsStartedAtRelativeMinimum();

  const assetSections: FormSections = useMemo(() => {
    const startedAtBounds = resolveAssetStartedAtBounds(assetStartedAtLimit, {
      ignoreRelativeMinimum: skipRelativeMinimum,
    });

    return resolveSectionsFieldShortcuts(rawAssetSections, assetFieldsConfig ?? [])
      .map(section => ({
        ...section,
        /**
         * WARNING disallow editing startedAt if asset was created not on the current branch, to avoid being blocked in MTA
         */
        fields: section.fields
          .filter(({ name }) => name !== 'startedAt' || asset?.createdOnBranch)
          .map(field =>
            field.name === 'startedAt' && (field.type === 'datetime' || field.type === 'date')
              ? {
                  ...field,
                  min: laterBound(startedAtBounds.min, field.min),
                  max: earlierBound(startedAtBounds.max, field.max),
                }
              : field,
          ),
      }))
      .filter(section => section.fields.some(f => !f.hidden));
  }, [
    rawAssetSections,
    asset?.createdOnBranch,
    assetFieldsConfig,
    assetStartedAtLimit,
    skipRelativeMinimum,
  ]);

  const additionalQuoteComponentsSections: FormSections = useMemo(
    () =>
      rawAdditionalQuoteComponentsSections
        .map(section => ({
          ...section,
          fields: section.fields.filter(f => !f.hidden),
        }))
        .filter(
          section =>
            section.fields.some(f => !f.hidden) &&
            !(section.forTypes && !section.forTypes.includes(firmType ?? '')),
        ),
    [JSON.stringify(rawAdditionalQuoteComponentsSections), firmType],
  );

  const manualQuoteSections: FormSections = useMemo(
    () =>
      rawManualQuoteSections
        .map(section => ({
          ...section,
          fields: section.fields.filter(f => !f.hidden),
        }))
        .filter(section => section.fields.some(f => !f.hidden)),
    [JSON.stringify(rawManualQuoteSections)],
  );

  const policyFields = useMemo(() => buildAllFieldsFromSections(policySections), [policySections]);
  const assetFields = useMemo(() => buildAllFieldsFromSections(assetSections), [assetSections]);
  const additionalQuoteComponentsFields = useMemo(
    () =>
      showWaitingForQuote
        ? EMPTY_FIELDS
        : buildAllFieldsFromSections(additionalQuoteComponentsSections),
    [showWaitingForQuote, additionalQuoteComponentsSections],
  );
  const manualQuoteFields = useMemo(
    () => (showWaitingForQuote ? EMPTY_FIELDS : buildAllFieldsFromSections(manualQuoteSections)),
    [showWaitingForQuote, manualQuoteSections],
  );

  const form = useForm({
    mode: 'all',
    values: {
      tier: asset?.quote?.options?.tier ?? quoteConfig?.defaultConfig.tier,
      excess: asset?.quote?.options?.excess ?? quoteConfig?.defaultConfig.excess,
      ...buildFormValuesFromFields(policyFields, policy),
      ...buildFormValuesFromFields(assetFields, asset),
      ...buildFormValuesFromFields(additionalQuoteComponentsFields, policy),
      ...buildFormValuesFromFields(manualQuoteFields, policy),
    } as Record<string, unknown>,
    resolver: getResolver([
      ...policyFields,
      ...assetFields,
      ...additionalQuoteComponentsFields,
      ...manualQuoteFields,
    ]),
  });

  const formValues = form.watch();

  const formValuesKey = useMemo(
    () =>
      JSON.stringify(
        Object.entries(formValues)
          .filter(([key]) => {
            const allFields = [
              ...policyFields,
              ...assetFields,
              ...additionalQuoteComponentsFields,
              ...manualQuoteFields,
            ];
            const fieldNames = new Set(allFields.map(f => f.name));
            return fieldNames.has(key) || key.startsWith('peril-');
          })
          .sort(([a], [b]) => a.localeCompare(b))
          .map(([key, value]) => [key, value]),
      ),
    [policyFields, assetFields, additionalQuoteComponentsFields, manualQuoteFields, formValues],
  );

  const payload = useMemo(
    () =>
      buildPayloadFromFields(
        [...policyFields, ...assetFields, ...additionalQuoteComponentsFields, ...manualQuoteFields],
        formValues,
      ) as {
        fees?: BrokerageFeeOnPolicy[];
        periodDuration?: Duration;
        customFields?: CustomFieldDto[];
      },
    [formValuesKey],
  );

  const fees = useMemo(() => payload.fees, [formValuesKey]);

  const additionalQuoteComponents = useMemo(
    () => getAdditionalQuoteComponentsFromFormPayload(payload),
    [formValuesKey],
  );

  const additionalQuotePercentages = useMemo(
    () => getAdditionalQuotePercentagesFromFormPayload(payload),
    [formValuesKey],
  );

  const { data: calculatedQuotes, isLoading: isCalculatingQuotesLoading } = useCalculateQuotes({
    productId: policy.productId,
    policyId: policy.id,
    branchId: branch?.id!,
    assetIds,
    brokerageFeesOverride: fees,
    periodDurationOverride: payload.periodDuration,
    customFieldsToOverride: payload.customFields,
    additionalQuoteComponents,
    additionalQuotePercentages,
    prorated: true,
  });

  /**
   * Mutations
   */
  const { mutateAsync: updateAsset } = useAssetControllerEditAsset({
    client: { client: clientWithFork },
  });

  const { mutateAsync: updatePolicy } = usePolicyControllerEditPolicy({
    client: { client: clientWithFork },
  });

  const { mutateAsync: updatePolicyFees } = usePolicyControllerSetPolicyFees({
    client: { client: clientWithFork },
  });

  const { mutateAsync: manualQuotePolicy } = usePolicyControllerManualQuotePolicy({
    client: { client: clientWithFork },
    mutation: {
      onError: error => {
        const detailedError = error as { errorResponse?: { data?: { code?: string } } };

        if (detailedError?.errorResponse?.data?.code === 'NO_CHANGES_TO_QUOTE') {
          toast({
            variant: 'warning',
            title: 'Prime inchangée',
            description: "Aucun champ tarifant n'a été modifié lors de cet avenant.",
          });

          if (mode === 'stepper') {
            stepper.next();
          }

          return;
        }

        throw error;
      },
    },
  });

  const { mutateAsync: updateQuotePolicy } = usePolicyControllerUpdatePolicyQuote({
    client: { client: clientWithFork },
  });

  const handleFieldsUpdate = async (
    policyFields: Fields,
    assetFields: Fields,
    data: Record<string, unknown>,
    dirtyFields: Record<string, boolean | undefined>,
  ) => {
    let mutationBranchId: string = branch.id;

    if (policyFields.length > 0 && areFieldsDirty(policyFields, dirtyFields)) {
      const { fees, ...policyBody } = buildPayloadFromFields(
        policyFields,
        data,
        dirtyFields,
      ) as Partial<EditPolicyInputBody> & { fees?: BrokerageFeeOnPolicy[] };

      if (!isEmpty(policyBody)) {
        const response = await updatePolicy({
          policyId: policy.id,
          params: { branchId: mutationBranchId },
          data: policyBody,
        });

        const newBranchId = getNewBranchIdAfterFork(response);
        if (newBranchId) {
          mutationBranchId = newBranchId;
        }
      }

      // updatePolicyFees doesn't have the same restrictions as updatePolicy on QUOTED status,
      // and thus must be perform after instead of before to avoid losing updates on fork
      if (fees !== undefined) {
        const response = await updatePolicyFees({
          policyId: policy.id,
          params: { branchId: mutationBranchId },
          data: { fees },
        });

        const newBranchId = getNewBranchIdAfterFork(response);
        if (newBranchId) {
          mutationBranchId = newBranchId;
        }
      }
    }

    if (assetFields.length > 0 && areFieldsDirty(assetFields, dirtyFields)) {
      for (const assetId of assetIds) {
        // we don't want to update by mistake assets that are being replaced
        const isBeingReplaced = assets?.some(asset => asset.originAssetId === assetId);
        if (isBeingReplaced) continue;

        const response = await updateAsset({
          assetId,
          params: {
            branchId: mutationBranchId,
            skipStartedAtRelativeMinimum: skipRelativeMinimum || undefined,
          },
          data: buildPayloadFromFields(assetFields, data, dirtyFields),
        });

        const newBranchId = getNewBranchIdAfterFork(response);
        if (newBranchId) {
          mutationBranchId = newBranchId;
        }
      }
    }

    return mutationBranchId;
  };

  const onSubmit = form.handleSubmit(async data => {
    const { dirtyFields } = form.formState;
    const additionalQuoteComponentsDirtyFields = markSeededDefaultsDirty(
      additionalQuoteComponentsFields,
      policy,
      dirtyFields,
    );

    let mutationBranchId: string = await handleFieldsUpdate(
      policyFields,
      assetFields,
      data,
      dirtyFields,
    );

    // Save policy-level fields (customFields, fees) from additionalQuoteComponentsSections.
    // These are distinct from quote components/percentages and must go through the policy endpoints.

    if (
      additionalQuoteComponentsFields.length > 0 &&
      areFieldsDirty(additionalQuoteComponentsFields, additionalQuoteComponentsDirtyFields)
    ) {
      const { fees: additionalFees, customFields: additionalCustomFields } = buildPayloadFromFields(
        additionalQuoteComponentsFields,
        data,
        additionalQuoteComponentsDirtyFields,
      ) as { customFields?: CustomFieldDto[]; fees?: BrokerageFeeOnPolicy[] };

      if (additionalCustomFields?.length) {
        const response = await updatePolicy({
          policyId: policy.id,
          params: { branchId: mutationBranchId },
          data: { customFields: additionalCustomFields },
        });
        const newBranchId = getNewBranchIdAfterFork(response);
        if (newBranchId) mutationBranchId = newBranchId;
      }

      if (additionalFees !== undefined) {
        const response = await updatePolicyFees({
          policyId: policy.id,
          params: { branchId: mutationBranchId },
          data: { fees: additionalFees },
        });
        const newBranchId = getNewBranchIdAfterFork(response);
        if (newBranchId) mutationBranchId = newBranchId;
      }
    }

    const additionalQuoteComponentsPayload = buildPayloadFromFields(
      additionalQuoteComponentsFields,
      data,
    );

    const hasDirtyFieldsForQuote =
      form.getFieldState('tier')?.isDirty ||
      form.getFieldState('excess')?.isDirty ||
      Object.keys(formValues)
        .filter(key => key.startsWith('peril-'))
        .some(key => form.getFieldState(key)?.isDirty);
    const hasDirtyFieldsForManualQuote = areFieldsDirty(manualQuoteFields, dirtyFields);
    const isPolicyStatusNotQuoted = policy.status !== 'POLICY_QUOTED';

    let newBranchId: string | undefined;
    if (isPolicyStatusNotQuoted || hasDirtyFieldsForQuote || hasDirtyFieldsForManualQuote) {
      const manualQuotePayload = buildPayloadFromFields(manualQuoteFields, data);

      const response = await manualQuotePolicy({
        policyId: policy.id,
        params: { branchId: mutationBranchId },
        data: {
          components: [
            ...getAdditionalQuoteComponentsFromFormPayload(manualQuotePayload),
            ...getAdditionalQuoteComponentsFromFormPayload(additionalQuoteComponentsPayload),
          ],
          percentages: [
            ...getAdditionalQuotePercentagesFromFormPayload(manualQuotePayload),
            ...getAdditionalQuotePercentagesFromFormPayload(additionalQuoteComponentsPayload),
          ],
        },
      });

      newBranchId = getNewBranchIdAfterFork(response);
    } else if (
      areFieldsDirty(additionalQuoteComponentsFields, additionalQuoteComponentsDirtyFields)
    ) {
      const response = await updateQuotePolicy({
        policyId: policy.id,
        params: { branchId: mutationBranchId },
        data: {
          components: getAdditionalQuoteComponentsFromFormPayload(additionalQuoteComponentsPayload),
          percentages: getAdditionalQuotePercentagesFromFormPayload(
            additionalQuoteComponentsPayload,
          ),
        },
      });

      newBranchId = getNewBranchIdAfterFork(response);
    }

    if (newBranchId) {
      mutationBranchId = newBranchId;
    }

    if (mode === 'stepper' && stepper) {
      stepper.next(mutationBranchId ? { branchId: mutationBranchId } : undefined);
    }
  });

  const centerSection = (
    <Stack>
      {additionalComponent}
      {showWaitingForQuote ? (
        <WaitingForQuoteCard
          policy={policy}
          asset={asset}
          assets={assets}
          branch={branch}
          quoteConfig={quoteConfig}
          policySections={policySections}
          assetSections={assetSections}
        />
      ) : (
        <>
          {disabled && <QuoteDerogationLockNotice />}
          {assetSections.length > 0 && (
            <FormBuilderSections sections={assetSections} externalData={policy} />
          )}
          {policySections.length > 0 && (
            <FormBuilderSections
              sections={policySections}
              externalData={{ ...policy, asset, branchId: branch?.id }}
              disabled={disabled}
            />
          )}
          {manualQuoteSections.length > 0 && firmType === 'WHOLESALE' && (
            <FormBuilderSections sections={manualQuoteSections} externalData={policy} />
          )}
          {additionalQuoteComponentsSections.length > 0 && (
            <FormBuilderSections
              sections={additionalQuoteComponentsSections}
              externalData={policy}
            />
          )}
          {Boolean(nonDelegatedDocuments.length) && firmType === 'WHOLESALE' && (
            <DocumentsUploadPolicyModule
              displayContext="broker"
              documentsLists={
                nonDelegatedDocuments?.map(category => ({ category, documentType: category })) ?? []
              }
              categoryType="nonDelegatedDocuments"
              useBranchId={Boolean(branch.id)}
              authorizedFormats={{
                'application/pdf': ['.pdf'],
              }}
            />
          )}
        </>
      )}
    </Stack>
  );

  if (isCheckingDerogationLoading || showWaitingForQuote === undefined) {
    return <LoadingCard />;
  }

  return (
    <QuoteContext.Provider
      value={{
        policy,
        asset,
        customer,
        quoteConfig,
        calculatedQuotes,
        isLoading: isCalculatingQuotesLoading,
      }}
    >
      <Form {...form}>
        <form className="flex flex-col gap-4" onSubmit={e => e.preventDefault()}>
          {showQuoteRecap === 'aside' && !showWaitingForQuote ? (
            <Stack className="xl:flex-row">
              <div className="w-full xl:w-5/7 min-w-0">{centerSection}</div>
              <Stack className="w-full xl:w-2/7 sticky top-4 self-start">
                <QuoteRecapModule />
                {helper && (
                  <Card>
                    <CardContent className="pt-6">{helper}</CardContent>
                  </Card>
                )}
              </Stack>
            </Stack>
          ) : showQuoteRecap === 'inline' && !showWaitingForQuote ? (
            <>
              {centerSection}
              <QuoteRecapModule />
            </>
          ) : (
            centerSection
          )}
          <FormBuilderFooter
            mode={mode}
            showPreviousButton={showPreviousButton}
            form={form}
            onSubmit={onSubmit}
            showNextButton={!showWaitingForQuote}
          />
        </form>
      </Form>
    </QuoteContext.Provider>
  );
}
